import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { trackInitiateCheckout } from "../lib/metaPixel";
import { generateMetaEventId } from "../lib/metaEvents";
import { COUNTRIES, JOB_ROLES } from "../lib/countries";
import {
  CheckCircle2, ArrowRight, Lock, ShieldCheck, ArrowLeft, Clock, Globe2,
} from "lucide-react";

function formatGBP(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
}

const PACKAGE_TITLES = {
  virtual_full: { title: "Full Access", subtitle: "For consultants, specialists and everyone else." },
  virtual_concession: { title: "Nurses & Trainees", subtitle: "Concession rate for nurses, resident doctors and trainees." },
};

export default function Checkout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const packageId = params.get("package") === "virtual_concession" ? "virtual_concession" : "virtual_full";

  const [pricing, setPricing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    job_role: "",
    job_role_other: "",
    hospital: "",
    country: "",
    confirmedRole: false,
  });

  useEffect(() => {
    api.get("/packages").then(r => setPricing(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm(f => ({ ...f, name: user.name || f.name, email: user.email || f.email }));
  }, [user]);

  // Pricing for the selected package (used in the summary panel + submit handler).
  const pricedAmount = pricing?.packages?.[packageId]?.current_amount;

  const pkg = pricing?.packages?.[packageId];
  const isConcession = packageId === "virtual_concession";
  const meta = PACKAGE_TITLES[packageId];

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (isConcession && !form.confirmedRole) {
      toast.error("Please confirm you are a nurse, resident doctor or trainee.");
      return;
    }
    if (!form.job_role) { toast.error("Please select your job role."); return; }
    if (form.job_role === "Other" && !form.job_role_other.trim()) {
      toast.error("Please tell us your role (or pick from the list).");
      return;
    }
    if (!form.hospital.trim()) { toast.error("Please enter your hospital or organisation."); return; }
    if (!form.country) { toast.error("Please select your country."); return; }
    setSubmitting(true);
    try {
      // Single event_id used by BOTH the browser Pixel InitiateCheckout below
      // AND the server-side CAPI mirror — Meta dedupes on (event_id, event_name).
      const ic_event_id = generateMetaEventId();
      trackInitiateCheckout({
        value: pricedAmount,
        currency: "GBP",
        content_name: PACKAGE_TITLES[packageId]?.title || packageId,
        content_ids: [packageId],
        event_id: ic_event_id,
      });

      const { data } = await api.post("/payments/checkout/session", {
        package_id: packageId,
        origin_url: window.location.origin,
        confirmed_role: form.confirmedRole,
        email: form.email.trim(),
        name: form.name.trim(),
        job_role: form.job_role,
        job_role_other: form.job_role === "Other" ? form.job_role_other.trim() : "",
        hospital: form.hospital.trim(),
        country: form.country,
        meta_event_id: ic_event_id,
        meta_event_source_url: window.location.href,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (pricing && !pricing.sales_open) {
    return (
      <div className="min-h-screen bg-[#F3F2F0]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Lock size={36} className="text-rose-600 mx-auto" strokeWidth={1.5} />
          <h1 className="mt-5 font-heading text-3xl tracking-tight font-medium text-slate-900">
            Ticket sales have closed
          </h1>
          <p className="mt-4 text-slate-600">
            The window for new purchases ended 24 hours after the conference. If you already hold a ticket, your portal access continues until 22 June 2027.
          </p>
          <Button onClick={() => navigate("/login")} className="mt-8 rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="closed-login-btn">
            Sign in to portal
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F2F0]" data-testid="checkout-page">
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <button onClick={() => navigate("/#tickets")}
                className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
                data-testid="checkout-back-link">
          <ArrowLeft size={14} /> Back to tickets
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* LEFT — Order summary */}
          <div className="lg:col-span-2 lg:order-1 order-2">
            <div className="rounded-3xl bg-white ring-1 ring-black/5 p-7 sticky top-24">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Order summary</div>
              <h2 className="mt-2 font-heading text-2xl tracking-tight font-medium text-slate-900" data-testid="checkout-package-title">
                DFTB26 Virtual
              </h2>
              <p className="text-sm text-slate-600 mt-1">{meta?.title} - {meta?.subtitle}</p>

              <div className="mt-6 flex items-baseline justify-between">
                <span className="text-sm text-slate-500">Today</span>
                <div className="text-right">
                  {pkg ? (
                    <>
                      <span className="font-heading text-4xl font-black tracking-tight text-slate-900" data-testid="checkout-amount">
                        {formatGBP(pkg.current_amount)}
                      </span>
                      {pricing?.is_early_bird && (
                        <div className="text-xs text-slate-400 line-through mt-0.5">{formatGBP(pkg.standard_amount)}</div>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-400 text-sm">Loading…</span>
                  )}
                </div>
              </div>

              {pricing?.is_early_bird && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#DC4F48] bg-[#DC4F48]/10 px-2.5 py-1 rounded-full">
                  Early-bird · ends 14 June
                </div>
              )}

              <div className="mt-7 border-t border-black/5 pt-5">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">Included</div>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {[
                    "3 days of live streaming (22-24 Jun 2026)",
                    "Every keynote, panel, and workshop",
                    "12-month replay access in the portal",
                    "Stream from anywhere in the world",
                    "Email login sent after checkout",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="text-[#28BBE3] flex-shrink-0 mt-1" strokeWidth={2} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck size={14} className="text-slate-400" />
                Secured by Stripe · Cards processed safely outside this site
              </div>
            </div>
          </div>

          {/* RIGHT — Buyer details form */}
          <div className="lg:col-span-3 lg:order-2 order-1">
            <div className="rounded-3xl bg-white ring-1 ring-black/5 p-7 sm:p-9">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Step 1 of 2</div>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900">
                Your details
              </h1>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Your login + receipt will be sent to this email. Use a real one - this is how you'll access the portal.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="checkout-form">
                <div>
                  <Label htmlFor="checkout-name" className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold">Full name</Label>
                  <Input
                    id="checkout-name" required minLength={2}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="e.g. Sarah Johnson"
                    className="mt-2 rounded-xl bg-[#F3F2F0] border-0 focus-visible:ring-[#28BBE3] py-6 px-4 text-base"
                    data-testid="checkout-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="checkout-email" className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold">Email address</Label>
                  <Input
                    id="checkout-email" type="email" required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@hospital.org"
                    className="mt-2 rounded-xl bg-[#F3F2F0] border-0 focus-visible:ring-[#28BBE3] py-6 px-4 text-base"
                    data-testid="checkout-email-input"
                  />
                  <p className="mt-1.5 text-xs text-slate-500">We'll send your receipt and portal login here.</p>
                </div>

                <div>
                  <Label htmlFor="checkout-role" className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold">Your role</Label>
                  <select
                    id="checkout-role" required
                    value={form.job_role}
                    onChange={(e) => update("job_role", e.target.value)}
                    data-testid="checkout-role-select"
                    className="mt-2 w-full rounded-xl bg-[#F3F2F0] border-0 py-[18px] px-4 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#28BBE3]"
                  >
                    <option value="">Select your role…</option>
                    {JOB_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {form.job_role === "Other" && (
                    <Input
                      value={form.job_role_other}
                      onChange={(e) => update("job_role_other", e.target.value)}
                      placeholder="Tell us your role"
                      className="mt-2 rounded-xl bg-[#F3F2F0] border-0 focus-visible:ring-[#28BBE3] py-6 px-4 text-base"
                      data-testid="checkout-role-other-input"
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="checkout-hospital" className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold">Hospital / Organisation</Label>
                  <Input
                    id="checkout-hospital" required minLength={2}
                    value={form.hospital}
                    onChange={(e) => update("hospital", e.target.value)}
                    placeholder="e.g. Royal Children's Hospital, Melbourne"
                    className="mt-2 rounded-xl bg-[#F3F2F0] border-0 focus-visible:ring-[#28BBE3] py-6 px-4 text-base"
                    data-testid="checkout-hospital-input"
                  />
                </div>

                <div>
                  <Label htmlFor="checkout-country" className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold">Country</Label>
                  <select
                    id="checkout-country" required
                    value={form.country}
                    onChange={(e) => update("country", e.target.value)}
                    data-testid="checkout-country-select"
                    className="mt-2 w-full rounded-xl bg-[#F3F2F0] border-0 py-[18px] px-4 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#28BBE3]"
                  >
                    <option value="">Select your country…</option>
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>

                {isConcession && (
                  <label className="flex items-start gap-3 rounded-xl bg-[#F3F2F0] px-4 py-4 cursor-pointer"
                         data-testid="checkout-concession-confirm-row">
                    <Checkbox
                      checked={form.confirmedRole}
                      onCheckedChange={(v) => update("confirmedRole", !!v)}
                      className="mt-0.5"
                      data-testid="checkout-concession-checkbox"
                    />
                    <span className="text-sm text-slate-700 leading-relaxed">
                      I confirm I am a <strong>nurse, resident doctor or trainee</strong>. Misuse may result in ticket revocation.
                    </span>
                  </label>
                )}

                <div className="pt-3">
                  <Button
                    type="submit"
                    disabled={submitting || !pkg || (isConcession && !form.confirmedRole)}
                    className="w-full rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white py-7 text-base font-semibold shadow-lg shadow-[#28BBE3]/25 disabled:opacity-60"
                    data-testid="checkout-continue-btn"
                  >
                    {submitting
                      ? "Redirecting to secure payment..."
                      : <>Continue to payment {pkg ? `· ${formatGBP(pkg.current_amount)}` : ""}<ArrowRight className="ml-2" size={17} strokeWidth={2} /></>
                    }
                  </Button>
                  <p className="mt-3 text-xs text-slate-500 text-center flex items-center justify-center gap-1.5">
                    <Lock size={11} /> You'll enter card details on Stripe's secure page. No card data touches our servers.
                  </p>
                  <p className="mt-2 text-xs text-slate-400 text-center" data-testid="checkout-support-line">
                    Issues with purchase? Email{" "}
                    <a href="mailto:registration@dontforgetthebubbles.com" className="text-[#28BBE3] hover:underline">
                      registration@dontforgetthebubbles.com
                    </a>
                  </p>
                </div>
              </form>

              {/* Trust footer */}
              <div className="mt-8 pt-7 border-t border-black/5 grid grid-cols-3 gap-4 text-center">
                <div className="text-slate-500">
                  <ShieldCheck size={18} className="text-[#28BBE3] mx-auto mb-1.5" strokeWidth={1.5} />
                  <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-700">Stripe-secured</div>
                </div>
                <div className="text-slate-500">
                  <Clock size={18} className="text-[#28BBE3] mx-auto mb-1.5" strokeWidth={1.5} />
                  <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-700">12-mo replay</div>
                </div>
                <div className="text-slate-500">
                  <Globe2 size={18} className="text-[#28BBE3] mx-auto mb-1.5" strokeWidth={1.5} />
                  <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-700">Worldwide</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
